import { IApplicationData } from 'types/applications';

import HeroImage from 'assets/applications/forth/hero.webp';
import FirstBlockImage from 'assets/applications/forth/first-block.webp';

import AutomationIcon from 'assets/applications/icons/automation.svg';
import DeveloperModeIcon from 'assets/applications/icons/developer-mode.svg';
import TickBoxIcon from 'assets/applications/icons/why-choose-us/tick-box.svg';
import CircleAroundIcon from 'assets/applications/icons/why-choose-us/circle-around.svg';
import CircleWithArrowIcon from 'assets/applications/icons/why-choose-us/circle-with-arrow.svg';
import ArrowUpIcon from 'assets/applications/icons/why-choose-us/arrow-up.svg';
import ThomasKImage from 'assets/avatars/ThomasK.svg';
import MichaelGrantImage from 'assets/avatars/MichaelGrant.svg';

export const forthApplicationData: IApplicationData = {
  hero: {
    title: 'Reinforcement Learning for Machine Control with Simulation-in-the-Loop',
    description: `
      Training control algorithms in the real world is risky, slow, and resource-intensive — especially 
      for complex autonomous systems. AILiveSim provides a powerful simulation-based environment to safely 
      and efficiently apply reinforcement learning (RL) to machine control. Our platform enables continuous 
      learning through simulated real-world interactions and automated feedback, accelerating the development 
      of adaptive, high-performing control strategies.
    `,
    videoSrc: 'https://www.ailivesim.com/Videos/AILiveSim_Navy.mp4',
    imageSrc: HeroImage,
  },
  firstTextImageData: {
    title: 'Simulation-in-the-Loop for Safer, Faster Control Training',
    description: `
      Reinforcement learning thrives on trial and error — but in high-risk domains, every mistake in the real 
      world comes with high costs. AILiveSim transforms training by enabling safe, repeatable experimentation 
      entirely in simulation, ensuring faster learning and better outcomes.
    `,
    imageSrc: FirstBlockImage,
    listItems: [
      'Real-world testing is expensive, limited, and potentially hazardous',
      'Manual tuning of control policies is slow and inefficient',
      'Lack of safe, scalable environments for iterative learning',
      'Difficulty validating performance across edge cases and dynamic conditions',
    ],
  },
  informationData: {
    title: 'Smarter, Scalable RL Training with AILiveSim ',
    description: `
      AILiveSim  delivers simulation-in-the-loop reinforcement learning, providing dynamic, 
      high-fidelity environments where AI systems can learn safely, iterate rapidly, and adapt 
      to diverse operational challenges.
    `,
    items: [
      {
        imageSrc: AutomationIcon,
        title: 'AI-Powered Training to Minimize Real-World Risk',
        description:
          'Develop and optimize complex control strategies entirely in simulation before real-world deployment.',
      },
      {
        imageSrc: DeveloperModeIcon,
        title: 'On-Demand Iteration for Faster Learning Cycles',
        description:
          'Run thousands of training episodes in parallel to accelerate policy refinement.',
      },
    ],
    listItems: [
      'Simulation-in-the-loop reinforcement learning for autonomous systems',
      'Automated feedback loops for rapid, risk-free training',
      'Iterative training and testing in dynamic virtual environments',
      'Performance analysis tools to evaluate and refine control strategies',
    ],
    listTitle: 'Key Capabilities of AILiveSim  for Control Optimization:',
  },
  whyChooseUsData: {
    title: 'Why Teams Choose AILiveSim  for Reinforcement Learning',
    blueBoxTitle: `
      Make simulation a core part of your reinforcement learning strategy. With AILiveSim , train smarter, scale faster, and deploy with confidence.
    `,
    items: [
      {
        title: 'Optimize Complex Control Tasks Without Real-World Risk',
        description: 'Eliminate safety hazards while enabling unrestricted testing.',
        imageSrc: TickBoxIcon,
      },
      {
        title: 'Accelerate Training Cycles Through Scalable Simulation',
        description: 'Leverage cloud or local computing to speed up learning.',
        imageSrc: CircleAroundIcon,
      },
      {
        title: 'Improve Resilience and Adaptability of Machine Behavior',
        description: 'Expose models to varied and unpredictable scenarios.',
        imageSrc: ArrowUpIcon,
      },
      {
        title: 'Fine-Tune Models with Real-Time Feedback',
        description: 'Use performance insights to continually refine and improve policies.',
        imageSrc: CircleWithArrowIcon,
      },
    ],
  },
  carouselData: [
    {
      authorImage: MichaelGrantImage,
      authorName: 'Michael Grant',
      authorRole: 'Innovation Lead at a major maritime OEM',
      text: '“Simulation allowed us to iterate quickly, validate safely, and deliver an intelligent co-captain experience the market hadn’t seen before.”',
    },
    {
      authorImage: ThomasKImage,
      authorName: 'Thomas K.',
      authorRole: 'Program Lead, Helicopter OEM',
      text: `"AILiveSim allowed us to simulate the unpredictable—from night-time flights near power lines to low-visibility urban environments. It's the only solution that offered the
        variability and realism our project demanded."`,
    },
  ],
  // Temporarily commented out until the client receives the data.
  // secondTextImageData: {
  //   title: 'Customer Case: Optimizing Autonomous Docking for Maritime Operations',
  //   description: `
  //     Discover how our client leveraged scenario-based simulation to enhance the performance
  //     and reliability of an advanced auto-docking system. This innovative approach not only
  //     streamlined operations but also ensured safety in challenging maritime environments.
  //   `,
  //   imageSrc: PlaceholderImage,
  // },
};
